/**
 * Which text colour actually reads on a given background.
 *
 * Needed anywhere a label is printed on a colour chosen for what it means
 * rather than for how text sits on it: machine badges, cable colours. White
 * text is not automatically readable on a saturated colour — measured against
 * real contrast ratios, a brightness threshold picked by eye got seven of the
 * eight machine colours wrong, putting white on backgrounds where it came out
 * below 2:1. Saturated mid-tones look bright without being light, which is
 * exactly where eyeballing it fails.
 *
 * So it measures. Two candidate inks, and the one with more contrast wins.
 */

export const INK_LIGHT = '#ffffff';
export const INK_DARK = '#1c1e24';

/**
 * WCAG relative luminance. Six-digit hex only — `parseInt('fff', 16)` is
 * #000fff, a dark blue, so the shorthand silently gives the wrong answer.
 */
export function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ''));
  // Nothing sensible to measure. White is the safer guess for a background we
  // cannot read, since it pushes the label to dark ink.
  if (!m) return 1;

  const n = parseInt(m[1], 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(v => v / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));

  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG contrast ratio between two colours, 1:1 to 21:1. */
export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** The more readable of the two inks on this background. */
export function readableInk(background) {
  return contrast(background, INK_DARK) >= contrast(background, INK_LIGHT)
    ? INK_DARK
    : INK_LIGHT;
}
